import { ImageActions } from './ImageActions';
import { EditorActions } from './EditorActions';
import { store } from '../../index';
import {
  updateImageDataByIdwithoutundo,
  updateAudioUrl,
  updateAnnotations,
} from '../../store/labels/actionCreators';
import { ImageData } from '../../store/labels/types';
import { getCokkie } from '../../views/login/Login';
import { AnnotationImageLoader } from '../imageRepository/AnnotationImageLoader';

type AnnotationRequest = { promise: Promise<boolean>; controller: AbortController };
const requests = new Map<string, AnnotationRequest>();
let generation = 0;
let selectedImageId: string | null = null;

const annotationFingerprint = (image: ImageData): string => JSON.stringify({
  labelRects: image.labelRects,
  labelPoints: image.labelPoints,
  labelPolygons: image.labelPolygons,
  labelPolylines: image.labelPolylines,
  labelLines: image.labelLines,
  labelTags: image.labelTags,
  flags: image.flags,
});

const currentImage = (imageId: string): ImageData | undefined =>
  store.getState().labels.imagesData.find((image) => image.image_id === imageId);

const setLoadState = (imageId: string, state: ImageData['annotationLoadState']): void => {
  const image = currentImage(imageId);
  if (!image || image.annotationLoadState === 'loaded') return;
  store.dispatch(updateImageDataByIdwithoutundo(imageId, {
    ...image,
    annotationLoaded: false,
    annotationLoadState: state,
  }));
};

/** Cancel requests and isolate responses belonging to an earlier task. */
export const resetAnnotationImageRequestState = (): void => {
  generation += 1;
  requests.forEach(({ controller }) => controller.abort());
  requests.clear();
  selectedImageId = null;
  AnnotationImageLoader.reset();
};

const fetchAnnotation = (index: number, taskId: string): Promise<boolean> => {
  const image = store.getState().labels.imagesData[index];
  if (!image) return Promise.resolve(false);
  if (image.annotationLoadState === 'loaded') return Promise.resolve(true);
  const imageId = image.image_id;
  const existing = requests.get(imageId);
  if (existing) return existing.promise;

  const requestGeneration = generation;
  const fingerprint = annotationFingerprint(image);
  const controller = new AbortController();
  const promise = (async (): Promise<boolean> => {
    try {
      const response = await fetch(
        `${window['ENV'].API_URL}/tasks/annotate/${taskId}/1/${index + 1}?image_id=${encodeURIComponent(imageId)}`,
        {
          headers: {
            Authorization: `Bearer ${getCokkie('jwtToken')}`,
            Accept: 'application/json',
          },
          signal: controller.signal,
        },
      );
      if (!response.ok) throw new Error(`Unable to load annotations (${response.status})`);
      const result = await response.json();
      const annotation = result.annotations?.[imageId];
      if (!annotation) throw new Error(`Annotation response did not include ${imageId}`);
      if (requestGeneration !== generation || store.getState().general.projectData.id !== taskId) {
        return false;
      }
      const latest = currentImage(imageId);
      if (!latest) return false;
      const changed = annotationFingerprint(latest) !== fingerprint;
      store.dispatch(updateImageDataByIdwithoutundo(imageId, {
        ...latest,
        ...(changed ? {} : {
          labelRects: annotation.labelRects || [],
          labelPoints: annotation.labelPoints || [],
          labelPolygons: annotation.labelPolygons || [],
          labelPolylines: annotation.labelPolylines || [],
          labelLines: annotation.labelLines || [],
          labelTags: annotation.labelTags || [],
          flags: annotation.flags || [],
          saved: annotation.saved || false,
        }),
        seen: selectedImageId === imageId ? true : latest.seen,
        annotationPrefetched: selectedImageId !== imageId,
        annotationLoaded: true,
        annotationLoadState: 'loaded',
      }));
      store.dispatch(updateAnnotations({
        ...store.getState().labels.annotations,
        [imageId]: annotation,
      }));
      if (selectedImageId === imageId) {
        store.dispatch(updateAudioUrl(result.download_info?.audio_url));
      }
      return true;
    } catch (error) {
      if (requestGeneration === generation && !controller.signal.aborted) {
        console.error('Error loading annotations:', error);
        setLoadState(imageId, 'error');
      }
      return false;
    } finally {
      if (requests.get(imageId)?.controller === controller) requests.delete(imageId);
    }
  })();
  requests.set(imageId, { promise, controller });
  return promise;
};

/** Load remaining annotations in three background lanes after the editor opens. */
export const preloadAnnotationImages = (taskId: string): void => {
  const requestGeneration = generation;
  const images = store.getState().labels.imagesData;
  let nextIndex = 0;
  const worker = async (): Promise<void> => {
    while (nextIndex < images.length && requestGeneration === generation) {
      const index = nextIndex++;
      if (images[index].annotationLoadState === 'loaded') continue;
      await fetchAnnotation(index, taskId);
    }
  };
  for (let lane = 0; lane < Math.min(3, images.length); lane += 1) {
    void worker();
  }
};

/** Select an image while its annotation request is pending. */
export const UpdateImageList = async (index: number): Promise<void> => {
  const state = store.getState();
  const images = state.labels.imagesData;
  if (!images || index < 0 || index >= images.length) return;
  const imageId = images[index].image_id;
  selectedImageId = imageId;
  const requestGeneration = generation;
  EditorActions.setViewPortActionsDisabledStatus(true);
  ImageActions.getImageByIndex(index);
  EditorActions.setViewPortActionsDisabledStatus(true);
  AnnotationImageLoader.prefetch(images, index);

  if (images[index].annotationLoadState === 'loaded') {
    const latest = currentImage(imageId);
    if (latest && (!latest.seen || latest.annotationPrefetched)) {
      store.dispatch(updateImageDataByIdwithoutundo(imageId, {
        ...latest,
        seen: true,
        annotationPrefetched: false,
      }));
    }
    EditorActions.setViewPortActionsDisabledStatus(false);
    return;
  }
  setLoadState(imageId, 'loading');
  const loaded = await fetchAnnotation(index, state.general.projectData.id);
  if (requestGeneration !== generation || selectedImageId !== imageId) return;
  EditorActions.setViewPortActionsDisabledStatus(!loaded);
};
